/**
 * Antigüedad de Saldo x Mayor — migración web de ares9725.
 *
 * Aging de facturas a crédito del módulo Ventas x Mayor: buckets
 * 0-30/31-60/61-90/+90 calculados por el backend a la fecha de corte
 * (modo "A la fecha") o sobre un rango de fechas de factura. Vista
 * detallada (por factura) y resumida (por cliente). Filtro opcional
 * por cliente. Exporta a Excel e imprime.
 *
 * Los tramos son DÍAS VENCIDOS de cada cuota (mismo criterio que el estado
 * de cuenta) y muestran SOLO la mora: lo que aún no vence está en el
 * Balance pero fuera de los tramos, así que Balance >= suma de tramos.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { hayOtraMoneda, monedaLabel } from "lib/moneda";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

/**
 * Total del reporte. `lineas` reemplaza al valor único cuando el reporte trae
 * facturas fuera de la moneda base: una línea por moneda, porque sumarlas en
 * una sola cifra no dice cuánto hay que cobrar en cada una. `nota` lleva el
 * equivalente convertido, que es el que cuadra con el resto del documento.
 */
function ResumenChip({ label, value, lineas, nota, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      {lineas?.length ? (
        lineas.map((l) => (
          <Typography key={l} sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>
            {l}
          </Typography>
        ))
      ) : (
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
      )}
      {nota ? (
        <Typography sx={{ fontSize: "0.66rem", color: FP.color.textMuted }}>{nota}</Typography>
      ) : null}
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
// `totalesextra` son las filas de saldo por moneda que van bajo el total.
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales, totalesextra) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales, totalesextra });
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function AntiguedadSaldoXmayor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tab, setTab] = useState(0);
  const [modo, setModo] = useState("alafecha");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);

  const [facturas, setFacturas] = useState([]);
  const [porcliente, setPorcliente] = useState([]);
  const [totales, setTotales] = useState(null);
  // Saldo agrupado por la moneda en que se emitió cada factura: el total
  // general va convertido a la base, esto dice cuánto hay que cobrar en
  // pesos y cuánto en dólares (el dólar se cobra en dólares).
  const [pormoneda, setPormoneda] = useState([]);
  // Moneda en la que el backend ya expresó todos los importes.
  const [base, setBase] = useState("DOP");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    post("/api/xmayor_clientes", {}).then(({ ok, data }) => {
      if (ok) setClientes(data.clientes || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/xmayor_aging", {
        modo,
        fechadesde: fecha1,
        fechahasta: fecha2,
        cliente: cliente?.id || "",
      });
      if (ok) {
        setFacturas(data.facturas || []);
        setPorcliente(data.porcliente || []);
        setTotales(data.totales || null);
        setPormoneda(data.pormoneda || []);
        setBase(data.base || "DOP");
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  // ¿Alguna factura se emitió en otra moneda? Si no, todo está en la base y
  // el reporte se ve como siempre. Si sí, cada fila va en SU moneda y el
  // total general se rotula como lo que es: un equivalente convertido.
  const mezcla = hayOtraMoneda(facturas, base);
  const etiquetaTotal = mezcla ? `TOTAL (equivalente en ${monedaLabel(base)})` : "TOTAL";
  const etiquetaSaldo = (m) => `SALDO ${monedaLabel(m.moneda)}`;
  // Una fila de total por moneda emitida, solo cuando hay más de una: con
  // una sola sería repetir el total general con otro nombre.
  const filasPorMoneda = (fila) => (mezcla ? pormoneda.map(fila) : []);
  // Importe de una factura para el impreso/Excel: en la moneda en que se
  // emitió cuando el reporte trae varias, con el símbolo delante.
  const montoFila = (f, campo) =>
    mezcla ? `${monedaLabel(f.moneda)} ${fmtMoney(f[`${campo}mon`])}` : fmtMoney(f[campo]);
  const valorFila = (f, campo) => (mezcla ? f[`${campo}mon`] : f[campo]);
  // Los totales llevan símbolo solo con mezcla: el general va en la base y
  // cada fila de desglose en su propia moneda.
  const montoBase = (v) => (mezcla ? `${monedaLabel(base)} ${fmtMoney(v)}` : fmtMoney(v));
  const montoMoneda = (m, campo) => `${monedaLabel(m.moneda)} ${fmtMoney(m[campo])}`;
  // Chips: una línea por moneda emitida, con el convertido como referencia.
  const lineasMoneda = (campo) => (mezcla ? pormoneda.map((m) => montoMoneda(m, campo)) : null);
  const notaEquivalente = (v) => (mezcla ? `≈ ${monedaLabel(base)} ${fmtMoney(v)}` : null);

  const subtitulo = `${
    modo === "alafecha"
      ? `Al ${fechaDMY(fecha2)}`
      : `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`
  }${cliente ? ` · ${cliente.descrip}` : ""}`;

  const exportar = () => {
    const stamp = hoyISO();
    let hoja;
    if (tab === 0) {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Antigüedad de Saldo x Mayor — Detallado"],
        [subtitulo],
        [],
        [
          "Fact#",
          "Fecha",
          "Suplidor",
          "Cliente",
          "Moneda",
          "Valor",
          "Aplicado",
          "Avance",
          "Balance",
          "Días",
          "Atraso",
          "0-30",
          "31-60",
          "61-90",
          "+90",
        ],
        ...facturas.map((f) => [
          f.id,
          f.fecha,
          f.suplidor,
          f.cliente,
          monedaLabel(mezcla ? f.moneda : base),
          valorFila(f, "valor"),
          valorFila(f, "vplicado"),
          valorFila(f, "vavance"),
          valorFila(f, "balance"),
          f.dias,
          f.atraso,
          valorFila(f, "t0a30"),
          valorFila(f, "t31a60"),
          valorFila(f, "t61a90"),
          valorFila(f, "tmas90"),
        ]),
        [],
        [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          etiquetaTotal,
          totales?.balance || 0,
          "",
          "",
          totales?.t0a30 || 0,
          totales?.t31a60 || 0,
          totales?.t61a90 || 0,
          totales?.tmas90 || 0,
        ],
        ...filasPorMoneda((m) => [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          etiquetaSaldo(m),
          m.balance,
          "",
          "",
          m.t0a30,
          m.t31a60,
          m.t61a90,
          m.tmas90,
        ]),
      ]);
    } else {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Antigüedad de Saldo x Mayor — Resumido"],
        [subtitulo],
        [],
        ["Suplidor", "Cliente", "Atraso", "0-30", "31-60", "61-90", "+90", "Balance"],
        ...porcliente.map((c) => [
          c.suplidor,
          c.cliente,
          c.atraso,
          c.t0a30,
          c.t31a60,
          c.t61a90,
          c.tmas90,
          c.balance,
        ]),
        [],
        [
          "",
          etiquetaTotal,
          "",
          totales?.t0a30 || 0,
          totales?.t31a60 || 0,
          totales?.t61a90 || 0,
          totales?.tmas90 || 0,
          totales?.balance || 0,
        ],
        ...filasPorMoneda((m) => [
          "",
          etiquetaSaldo(m),
          "",
          m.t0a30,
          m.t31a60,
          m.t61a90,
          m.tmas90,
          m.balance,
        ]),
      ]);
    }
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, tab === 0 ? "Detallado" : "Resumido");
    XLSX.writeFile(libro, `AntiguedadSaldoXmayor_${stamp}.xlsx`);
  };

  const imprimir = () => {
    if (tab === 0) {
      imprimirListado(
        "Antigüedad de Saldo x Mayor — Detallado",
        subtitulo,
        [
          "Fact#",
          "Fecha",
          "Suplidor",
          "Cliente",
          "Balance",
          "Días",
          "Atraso",
          "0-30",
          "31-60",
          "61-90",
          "+90",
        ],
        facturas.map((f) => [
          f.id,
          f.fecha,
          f.suplidor,
          f.cliente,
          montoFila(f, "balance"),
          f.dias,
          f.atraso,
          montoFila(f, "t0a30"),
          montoFila(f, "t31a60"),
          montoFila(f, "t61a90"),
          montoFila(f, "tmas90"),
        ]),
        [
          "",
          "",
          "",
          etiquetaTotal,
          montoBase(totales?.balance),
          "",
          "",
          montoBase(totales?.t0a30),
          montoBase(totales?.t31a60),
          montoBase(totales?.t61a90),
          montoBase(totales?.tmas90),
        ],
        filasPorMoneda((m) => [
          "",
          "",
          "",
          etiquetaSaldo(m),
          montoMoneda(m, "balance"),
          "",
          "",
          montoMoneda(m, "t0a30"),
          montoMoneda(m, "t31a60"),
          montoMoneda(m, "t61a90"),
          montoMoneda(m, "tmas90"),
        ])
      );
    } else {
      imprimirListado(
        "Antigüedad de Saldo x Mayor — Resumido",
        subtitulo,
        ["Suplidor", "Cliente", "Atraso", "0-30", "31-60", "61-90", "+90", "Balance"],
        porcliente.map((c) => [
          c.suplidor,
          c.cliente,
          c.atraso,
          fmtMoney(c.t0a30),
          fmtMoney(c.t31a60),
          fmtMoney(c.t61a90),
          fmtMoney(c.tmas90),
          fmtMoney(c.balance),
        ]),
        [
          "",
          etiquetaTotal,
          "",
          montoBase(totales?.t0a30),
          montoBase(totales?.t31a60),
          montoBase(totales?.t61a90),
          montoBase(totales?.tmas90),
          montoBase(totales?.balance),
        ],
        filasPorMoneda((m) => [
          "",
          etiquetaSaldo(m),
          "",
          montoMoneda(m, "t0a30"),
          montoMoneda(m, "t31a60"),
          montoMoneda(m, "t61a90"),
          montoMoneda(m, "tmas90"),
          montoMoneda(m, "balance"),
        ])
      );
    }
  };

  // La columna Moneda solo aporta con mezcla: en un tenant que opera en una
  // sola sería el mismo valor repetido en cada fila.
  const colMoneda = {
    field: "moneda",
    headerName: "Moneda",
    width: 90,
    valueFormatter: (value) => monedaLabel(value),
    description: "Moneda de emisión. Los importes de la fila van en ella.",
  };

  // Con mezcla, cada fila se pinta en la moneda de SU factura (columnas
  // `*mon` del backend): un importe en pesos bajo una factura en dólares es
  // justo lo que confunde al cobrar. El resumen por cliente no puede hacerlo
  // —agrupa facturas de varias monedas— y se queda en el equivalente base.
  const moneyColFila = (field, headerName, width = 110) =>
    mezcla
      ? {
          field,
          headerName,
          width,
          align: "right",
          headerAlign: "right",
          valueGetter: (value, row) => row?.[`${field}mon`] ?? value,
          renderCell: (p) => `${monedaLabel(p.row.moneda)} ${fmtMoney(p.value)}`,
        }
      : moneyCol(field, headerName, width);

  const columnasFacturas = [
    { field: "id", headerName: "Fact#", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "suplidor", headerName: "Suplidor", width: 130 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 170 },
    ...(mezcla ? [colMoneda] : []),
    moneyColFila("valor", "Valor", mezcla ? 130 : 110),
    moneyColFila("vplicado", "Aplicado", mezcla ? 130 : 110),
    moneyColFila("vavance", "Avance", mezcla ? 130 : 110),
    moneyColFila("balance", "Balance", mezcla ? 130 : 110),
    { field: "dias", headerName: "Días", width: 70, align: "right", headerAlign: "right" },
    {
      field: "atraso",
      headerName: "Atraso",
      width: 80,
      align: "right",
      headerAlign: "right",
      description: "Días vencidos de la cuota más atrasada con saldo",
    },
    moneyColFila("t0a30", "0-30", mezcla ? 125 : 100),
    moneyColFila("t31a60", "31-60", mezcla ? 125 : 100),
    moneyColFila("t61a90", "61-90", mezcla ? 125 : 100),
    moneyColFila("tmas90", "+90", mezcla ? 125 : 100),
  ];

  const columnasPorcliente = [
    { field: "suplidor", headerName: "Suplidor", width: 140 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 200 },
    {
      field: "atraso",
      headerName: "Atraso",
      width: 85,
      align: "right",
      headerAlign: "right",
      description: "Días vencidos de la cuota más atrasada del cliente",
    },
    moneyCol("t0a30", "0-30", 115),
    moneyCol("t31a60", "31-60", 115),
    moneyCol("t61a90", "61-90", 115),
    moneyCol("tmas90", "+90", 115),
    moneyCol("balance", "Balance", 125),
  ];

  const hayDatos = tab === 0 ? facturas.length > 0 : porcliente.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Antigüedad de Saldo x Mayor"
        subtitle="Balances pendientes de facturas x mayor con la mora por días vencidos de cuota (0-30/31-60/61-90/+90)"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Modo"
                value={modo}
                onChange={(e) => setModo(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="alafecha">A la fecha</MenuItem>
                <MenuItem value="rango">Rango de fechas</MenuItem>
              </TextField>
            </Grid>
            {modo === "rango" && (
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  InputLabelProps={{ shrink: true }}
                  value={fecha1}
                  onChange={(e) => setFecha1(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                options={clientes}
                value={cliente}
                onChange={(_, v) => setCliente(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip
              label="BALANCE"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.balance)}`}
              lineas={lineasMoneda("balance")}
              nota={notaEquivalente(totales?.balance)}
            />
            <ResumenChip
              label="0-30 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.t0a30)}`}
              lineas={lineasMoneda("t0a30")}
              nota={notaEquivalente(totales?.t0a30)}
              color="#2E7D32"
            />
            <ResumenChip
              label="31-60 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.t31a60)}`}
              lineas={lineasMoneda("t31a60")}
              nota={notaEquivalente(totales?.t31a60)}
              color="#F9A825"
            />
            <ResumenChip
              label="61-90 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.t61a90)}`}
              lineas={lineasMoneda("t61a90")}
              nota={notaEquivalente(totales?.t61a90)}
              color="#EF6C00"
            />
            <ResumenChip
              label="+90 VENCIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.tmas90)}`}
              lineas={lineasMoneda("tmas90")}
              nota={notaEquivalente(totales?.tmas90)}
              color="#C62828"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
          >
            <Tab label="Detallado" />
            <Tab label="Resumido" />
          </Tabs>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={tab === 0 ? facturas : porcliente}
              columns={tab === 0 ? columnasFacturas : columnasPorcliente}
              getRowId={tab === 0 ? (r) => r.id : (r) => r.suplidor}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
